import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

// Tiny coordinator so we never stack more than one floating notification
// on top of page content. Components register a "slot" they want to
// occupy; the coordinator hands out a single visible slot based on
// priority. Trial tester banner is rendered inline (header strip), so
// it does not participate here — only floating overlays do.

export type NotificationSlot = "cookie" | "install";

// Lower number = wins when multiple are eligible at once. Consent must
// be addressed first (legal); install prompt is passive and waits.
const PRIORITY: Record<NotificationSlot, number> = {
  cookie: 0,
  install: 1,
};

interface Ctx {
  active: NotificationSlot | null;
  request: (slot: NotificationSlot) => void;
  release: (slot: NotificationSlot) => void;
  isActive: (slot: NotificationSlot) => boolean;
}

const NotificationContext = createContext<Ctx | null>(null);

export function NotificationCoordinator({ children }: { children: ReactNode }) {
  const [requested, setRequested] = useState<Set<NotificationSlot>>(new Set());

  const request = useCallback((slot: NotificationSlot) => {
    setRequested((prev) => {
      if (prev.has(slot)) return prev;
      const next = new Set(prev);
      next.add(slot);
      return next;
    });
  }, []);

  const release = useCallback((slot: NotificationSlot) => {
    setRequested((prev) => {
      if (!prev.has(slot)) return prev;
      const next = new Set(prev);
      next.delete(slot);
      return next;
    });
  }, []);

  const active = useMemo<NotificationSlot | null>(() => {
    let best: NotificationSlot | null = null;
    for (const slot of requested) {
      if (best === null || PRIORITY[slot] < PRIORITY[best]) best = slot;
    }
    return best;
  }, [requested]);

  const value = useMemo<Ctx>(
    () => ({
      active,
      request,
      release,
      isActive: (slot) => active === slot,
    }),
    [active, request, release],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotificationSlot(slot: NotificationSlot, wants: boolean) {
  const ctx = useContext(NotificationContext);
  useEffect(() => {
    if (!ctx) return;
    if (wants) {
      ctx.request(slot);
      return () => ctx.release(slot);
    }
    ctx.release(slot);
  }, [ctx, slot, wants]);
  return ctx?.isActive(slot) ?? wants;
}
